import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Edit3, CheckCircle, Copy, Check, Search, Share2, Twitter } from 'lucide-react';
import { Button } from '../ui/Button';

interface ArticleMetaEditorProps {
    htmlContent: string;
    onUpdateHtml: (newHtml: string) => void;
    initialMetadata?: {
        metaTitle?: string;
        metaDescription?: string;
        keywords?: string[];
    };
}

interface SeoFieldProps {
    label: string;
    value: string;
    fieldKey: string;
    maxLength?: number;
    rows?: number;
    placeholder?: string;
    isEditing: boolean;
    copiedField: string | null;
    onCopy: (text: string, fieldName: string) => void;
    onChange: (fieldKey: string, value: string) => void;
}

const getCharCountColor = (current: number, max: number) => {
    if (current === 0) return 'text-gray-400';
    if (current <= max) return 'text-green-600';
    return 'text-red-600';
};

const SeoField: React.FC<SeoFieldProps> = ({
    label,
    value,
    fieldKey,
    maxLength,
    rows = 1,
    placeholder = '',
    isEditing,
    copiedField,
    onCopy,
    onChange,
}) => {
    const inputId = `seo-field-${fieldKey}`;

    return (
        <div className="space-y-2">
            <div className="flex items-center justify-between">
                <label htmlFor={inputId} className="block text-sm font-medium text-gray-700">{label}</label>
                <div className="flex items-center gap-2">
                    {maxLength && (
                        <span className={`text-xs ${getCharCountColor(value.length, maxLength)}`}>
                            {value.length}/{maxLength}
                        </span>
                    )}
                    <button
                        onClick={() => onCopy(value, fieldKey)}
                        className="p-1 hover:bg-gray-100 rounded transition-colors"
                        title="Copiar"
                        type="button"
                    >
                        {copiedField === fieldKey ? (
                            <Check className="w-4 h-4 text-green-600" />
                        ) : (
                            <Copy className="w-4 h-4 text-gray-400" />
                        )}
                    </button>
                </div>
            </div>
            {isEditing ? (
                rows > 1 ? (
                    <textarea
                        id={inputId}
                        name={fieldKey}
                        value={value}
                        onChange={(e) => onChange(fieldKey, e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm"
                        rows={rows}
                        placeholder={placeholder}
                    />
                ) : (
                    <input
                        id={inputId}
                        name={fieldKey}
                        type="text"
                        value={value}
                        onChange={(e) => onChange(fieldKey, e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm"
                        placeholder={placeholder}
                    />
                )
            ) : (
                <div className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-white text-gray-700 text-sm break-words min-h-[38px]">
                    {value || <span className="text-gray-400 italic">Sin definir</span>}
                </div>
            )}
        </div>
    );
};

export const ArticleMetaEditor: React.FC<ArticleMetaEditorProps> = ({ htmlContent, onUpdateHtml, initialMetadata }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [metaTags, setMetaTags] = useState<{ [key: string]: string }>({});
    const [copiedField, setCopiedField] = useState<string | null>(null);

    // Extract article title from H1
    const extractArticleTitle = (html: string): string => {
        const h1Match = html.match(/<h1[^>]*>([^<]+)<\/h1>/i);
        return h1Match ? h1Match[1].trim() : '';
    };

    // Extract first paragraph for auto-description
    const extractFirstParagraph = (html: string): string => {
        const pMatch = html.match(/<p[^>]*>([^<]+)<\/p>/i);
        if (pMatch) {
            const text = pMatch[1].trim();
            return text.length > 155 ? text.substring(0, 152) + '...' : text;
        }
        return '';
    };

    // Helper to extract meta tags
    const extractMetaTags = (html: string) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const extracted: { [key: string]: string } = {};

        // 1. Title
        const title = doc.querySelector('title');
        if (title) extracted['tag:title'] = title.textContent || '';

        // 2. Canonical Link
        const canonical = doc.querySelector('link[rel="canonical"]');
        if (canonical) extracted['tag:canonical'] = canonical.getAttribute('href') || '';

        // 3. Meta Tags
        const metas = doc.getElementsByTagName('meta');
        Array.from(metas).forEach(meta => {
            const name = meta.getAttribute('name');
            const property = meta.getAttribute('property');
            const charset = meta.getAttribute('charset');
            const content = meta.getAttribute('content');

            if (charset) {
                extracted['attr:charset'] = charset;
            } else if (name && content) {
                extracted[`name:${name}`] = content;
            } else if (property && content) {
                extracted[`property:${property}`] = content;
            }
        });

        return extracted;
    };

    // Helper to update HTML with new meta tags
    const updateHtmlWithMeta = (originalHtml: string, tags: { [key: string]: string }) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(originalHtml, 'text/html');
        let head = doc.head;
        if (!head) {
            head = doc.createElement('head');
            doc.documentElement.insertBefore(head, doc.body);
        }

        // 1. Update Title
        if (tags['tag:title'] !== undefined) {
            let title = doc.querySelector('title');
            if (tags['tag:title']) {
                if (!title) {
                    title = doc.createElement('title');
                    head.appendChild(title);
                }
                title.textContent = tags['tag:title'];
            } else if (title) {
                title.remove();
            }
        }

        // 2. Update Canonical
        if (tags['tag:canonical'] !== undefined) {
            let canonical = doc.querySelector('link[rel="canonical"]');
            if (tags['tag:canonical']) {
                if (!canonical) {
                    canonical = doc.createElement('link');
                    canonical.setAttribute('rel', 'canonical');
                    head.appendChild(canonical);
                }
                canonical.setAttribute('href', tags['tag:canonical']);
            } else if (canonical) {
                canonical.remove();
            }
        }

        // 3. Update Metas (Remove existing handled ones first)
        const existingMetas = Array.from(doc.getElementsByTagName('meta'));
        existingMetas.forEach(meta => {
            if (meta.getAttribute('charset') && tags['attr:charset']) meta.remove();
            if (meta.getAttribute('name') && tags[`name:${meta.getAttribute('name')}`] !== undefined) meta.remove();
            if (meta.getAttribute('property') && tags[`property:${meta.getAttribute('property')}`] !== undefined) meta.remove();
        });

        // Add new/updated metas
        Object.entries(tags).forEach(([key, value]) => {
            if (!value) return;

            if (key === 'attr:charset') {
                const meta = doc.createElement('meta');
                meta.setAttribute('charset', value);
                head.appendChild(meta);
            } else if (key.startsWith('name:')) {
                const meta = doc.createElement('meta');
                meta.setAttribute('name', key.replace('name:', ''));
                meta.setAttribute('content', value);
                head.appendChild(meta);
            } else if (key.startsWith('property:')) {
                const meta = doc.createElement('meta');
                meta.setAttribute('property', key.replace('property:', ''));
                meta.setAttribute('content', value);
                head.appendChild(meta);
            }
        });

        return doc.documentElement.outerHTML;
    };

    // Generate SEO metas from article content
    const seoMetas = useMemo(() => {
        const articleTitle = extractArticleTitle(htmlContent);
        const autoDescription = extractFirstParagraph(htmlContent);

        return {
            // SEO Básico
            metaTitle: metaTags['tag:title'] || initialMetadata?.metaTitle || articleTitle || '',
            metaDescription: metaTags['name:description'] || initialMetadata?.metaDescription || autoDescription || '',
            keywords: metaTags['name:keywords'] || initialMetadata?.keywords?.join(', ') || '',
            robots: metaTags['name:robots'] || 'index, follow',

            // Open Graph
            ogTitle: metaTags['property:og:title'] || metaTags['tag:title'] || articleTitle || '',
            ogDescription: metaTags['property:og:description'] || metaTags['name:description'] || autoDescription || '',
            ogType: metaTags['property:og:type'] || 'article',
            ogImage: metaTags['property:og:image'] || '',
            ogUrl: metaTags['property:og:url'] || '',

            // Twitter Card
            twitterCard: metaTags['name:twitter:card'] || 'summary_large_image',
            twitterTitle: metaTags['name:twitter:title'] || metaTags['tag:title'] || articleTitle || '',
            twitterDescription: metaTags['name:twitter:description'] || metaTags['name:description'] || autoDescription || '',
            twitterImage: metaTags['name:twitter:image'] || metaTags['property:og:image'] || '',
        };
    }, [htmlContent, metaTags, initialMetadata]);

    // Editable SEO state
    const [editableSeo, setEditableSeo] = useState(seoMetas);

    useEffect(() => {
        setEditableSeo(seoMetas);
    }, [seoMetas]);

    useEffect(() => {
        const extracted = extractMetaTags(htmlContent);

        // Merge with initialMetadata if missing
        if (initialMetadata) {
            if (!extracted['tag:title'] && initialMetadata.metaTitle) {
                extracted['tag:title'] = initialMetadata.metaTitle;
            }
            if (!extracted['name:description'] && initialMetadata.metaDescription) {
                extracted['name:description'] = initialMetadata.metaDescription;
            }
            if (!extracted['name:keywords'] && initialMetadata.keywords && initialMetadata.keywords.length > 0) {
                extracted['name:keywords'] = initialMetadata.keywords.join(', ');
            }
        }

        setMetaTags(extracted);
    }, [htmlContent, initialMetadata]);

    const handleSave = () => {
        // Update metaTags with editable values
        const updatedTags = {
            ...metaTags,
            'tag:title': editableSeo.metaTitle,
            'name:description': editableSeo.metaDescription,
            'name:keywords': editableSeo.keywords,
            'name:robots': editableSeo.robots,
            'property:og:title': editableSeo.ogTitle,
            'property:og:description': editableSeo.ogDescription,
            'property:og:type': editableSeo.ogType,
            'property:og:image': editableSeo.ogImage,
            'property:og:url': editableSeo.ogUrl,
            'name:twitter:card': editableSeo.twitterCard,
            'name:twitter:title': editableSeo.twitterTitle,
            'name:twitter:description': editableSeo.twitterDescription,
            'name:twitter:image': editableSeo.twitterImage,
        };

        setMetaTags(updatedTags);
        const newHtml = updateHtmlWithMeta(htmlContent, updatedTags);
        onUpdateHtml(newHtml);
        setIsEditing(false);
    };

    const copyToClipboard = async (text: string, fieldName: string) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopiedField(fieldName);
            setTimeout(() => setCopiedField(null), 2000);
        } catch (err) {
            // Error copying to clipboard
        }
    };

    const copyAllSeoMetas = async () => {
        const allMetas = `<!-- SEO Básico -->
<title>${editableSeo.metaTitle}</title>
<meta name="description" content="${editableSeo.metaDescription}" />
<meta name="keywords" content="${editableSeo.keywords}" />
<meta name="robots" content="${editableSeo.robots}" />

<!-- Open Graph / Facebook -->
<meta property="og:type" content="${editableSeo.ogType}" />
<meta property="og:title" content="${editableSeo.ogTitle}" />
<meta property="og:description" content="${editableSeo.ogDescription}" />
${editableSeo.ogImage ? `<meta property="og:image" content="${editableSeo.ogImage}" />` : ''}
${editableSeo.ogUrl ? `<meta property="og:url" content="${editableSeo.ogUrl}" />` : ''}

<!-- Twitter Card -->
<meta name="twitter:card" content="${editableSeo.twitterCard}" />
<meta name="twitter:title" content="${editableSeo.twitterTitle}" />
<meta name="twitter:description" content="${editableSeo.twitterDescription}" />
${editableSeo.twitterImage ? `<meta name="twitter:image" content="${editableSeo.twitterImage}" />` : ''}`;

        await copyToClipboard(allMetas, 'all');
    };

    const handleFieldChange = useCallback((fieldKey: string, value: string) => {
        setEditableSeo(prev => ({ ...prev, [fieldKey]: value }));
    }, []);

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h3 className="font-semibold text-gray-700">Metas SEO para WordPress</h3>
                    <p className="text-sm text-gray-500 mt-1">
                        Copia estas etiquetas para pegarlas en Yoast SEO, Rank Math u otro plugin
                    </p>
                </div>
                <div className="flex gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={copyAllSeoMetas}
                        icon={copiedField === 'all' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    >
                        {copiedField === 'all' ? 'Copiado' : 'Copiar Todo'}
                    </Button>
                    <Button
                        variant={isEditing ? "primary" : "outline"}
                        size="sm"
                        onClick={() => isEditing ? handleSave() : setIsEditing(true)}
                        icon={isEditing ? <CheckCircle className="w-4 h-4" /> : <Edit3 className="w-4 h-4" />}
                    >
                        {isEditing ? 'Guardar' : 'Editar'}
                    </Button>
                </div>
            </div>

            {/* SEO Básico */}
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center gap-2 mb-4">
                    <Search className="w-5 h-5 text-blue-600" />
                    <h4 className="font-semibold text-blue-900">SEO Básico</h4>
                </div>
                <div className="space-y-4">
                    <SeoField
                        label="Meta Title (Título SEO)"
                        value={editableSeo.metaTitle}
                        fieldKey="metaTitle"
                        maxLength={60}
                        placeholder="Título optimizado para buscadores (máx 60 caracteres)"
                        isEditing={isEditing}
                        copiedField={copiedField}
                        onCopy={copyToClipboard}
                        onChange={handleFieldChange}
                    />
                    <SeoField
                        label="Meta Description"
                        value={editableSeo.metaDescription}
                        fieldKey="metaDescription"
                        maxLength={155}
                        rows={3}
                        placeholder="Descripción atractiva para los resultados de búsqueda (máx 155 caracteres)"
                        isEditing={isEditing}
                        copiedField={copiedField}
                        onCopy={copyToClipboard}
                        onChange={handleFieldChange}
                    />
                    <SeoField
                        label="Keywords (palabras clave)"
                        value={editableSeo.keywords}
                        fieldKey="keywords"
                        placeholder="keyword1, keyword2, keyword3"
                        isEditing={isEditing}
                        copiedField={copiedField}
                        onCopy={copyToClipboard}
                        onChange={handleFieldChange}
                    />
                    <SeoField
                        label="Robots"
                        value={editableSeo.robots}
                        fieldKey="robots"
                        placeholder="index, follow"
                        isEditing={isEditing}
                        copiedField={copiedField}
                        onCopy={copyToClipboard}
                        onChange={handleFieldChange}
                    />
                </div>
            </div>

            {/* Open Graph */}
            <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                <div className="flex items-center gap-2 mb-4">
                    <Share2 className="w-5 h-5 text-purple-600" />
                    <h4 className="font-semibold text-purple-900">Open Graph (Facebook, LinkedIn)</h4>
                </div>
                <div className="space-y-4">
                    <SeoField
                        label="og:title"
                        value={editableSeo.ogTitle}
                        fieldKey="ogTitle"
                        maxLength={60}
                        placeholder="Título para redes sociales"
                        isEditing={isEditing}
                        copiedField={copiedField}
                        onCopy={copyToClipboard}
                        onChange={handleFieldChange}
                    />
                    <SeoField
                        label="og:description"
                        value={editableSeo.ogDescription}
                        fieldKey="ogDescription"
                        maxLength={155}
                        rows={2}
                        placeholder="Descripción para compartir en redes"
                        isEditing={isEditing}
                        copiedField={copiedField}
                        onCopy={copyToClipboard}
                        onChange={handleFieldChange}
                    />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <SeoField
                            label="og:type"
                            value={editableSeo.ogType}
                            fieldKey="ogType"
                            placeholder="article"
                            isEditing={isEditing}
                            copiedField={copiedField}
                            onCopy={copyToClipboard}
                            onChange={handleFieldChange}
                        />
                        <SeoField
                            label="og:url"
                            value={editableSeo.ogUrl}
                            fieldKey="ogUrl"
                            placeholder="https://tudominio.com/articulo"
                            isEditing={isEditing}
                            copiedField={copiedField}
                            onCopy={copyToClipboard}
                            onChange={handleFieldChange}
                        />
                    </div>
                    <SeoField
                        label="og:image (URL de imagen destacada)"
                        value={editableSeo.ogImage}
                        fieldKey="ogImage"
                        placeholder="https://tudominio.com/imagen.jpg"
                        isEditing={isEditing}
                        copiedField={copiedField}
                        onCopy={copyToClipboard}
                        onChange={handleFieldChange}
                    />
                </div>
            </div>

            {/* Twitter Card */}
            <div className="p-4 bg-sky-50 rounded-lg border border-sky-200">
                <div className="flex items-center gap-2 mb-4">
                    <Twitter className="w-5 h-5 text-sky-600" />
                    <h4 className="font-semibold text-sky-900">Twitter Card</h4>
                </div>
                <div className="space-y-4">
                    <SeoField
                        label="twitter:card"
                        value={editableSeo.twitterCard}
                        fieldKey="twitterCard"
                        placeholder="summary_large_image"
                        isEditing={isEditing}
                        copiedField={copiedField}
                        onCopy={copyToClipboard}
                        onChange={handleFieldChange}
                    />
                    <SeoField
                        label="twitter:title"
                        value={editableSeo.twitterTitle}
                        fieldKey="twitterTitle"
                        maxLength={70}
                        placeholder="Título para Twitter"
                        isEditing={isEditing}
                        copiedField={copiedField}
                        onCopy={copyToClipboard}
                        onChange={handleFieldChange}
                    />
                    <SeoField
                        label="twitter:description"
                        value={editableSeo.twitterDescription}
                        fieldKey="twitterDescription"
                        maxLength={200}
                        rows={2}
                        placeholder="Descripción para Twitter"
                        isEditing={isEditing}
                        copiedField={copiedField}
                        onCopy={copyToClipboard}
                        onChange={handleFieldChange}
                    />
                    <SeoField
                        label="twitter:image"
                        value={editableSeo.twitterImage}
                        fieldKey="twitterImage"
                        placeholder="https://tudominio.com/imagen.jpg"
                        isEditing={isEditing}
                        copiedField={copiedField}
                        onCopy={copyToClipboard}
                        onChange={handleFieldChange}
                    />
                </div>
            </div>

            {/* Preview Google */}
            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <h4 className="font-semibold text-gray-700 mb-3">Vista previa en Google</h4>
                <div className="bg-white p-4 rounded border border-gray-200">
                    <div className="text-lg text-blue-700 hover:underline cursor-pointer truncate">
                        {editableSeo.metaTitle || 'Título de la página'}
                    </div>
                    <div className="text-sm text-green-700 truncate mt-1">
                        tudominio.com › articulo
                    </div>
                    <div className="text-sm text-gray-600 mt-1 line-clamp-2">
                        {editableSeo.metaDescription || 'Descripción de la página que aparecerá en los resultados de búsqueda...'}
                    </div>
                </div>
            </div>
        </div>
    );
};
